import { type RequirementIdentifier, useIntl } from '@capsicum/common';
import { useAtomValue } from 'jotai';
import { useCallback, useMemo } from 'react';

import { customRequirementsAtom } from '../data-access';

import { useGetRequirementStatusesQuery } from './../data-access/queries/useRequirementsQueries';

const KYC_REQUIREMENTS: Array<RequirementIdentifier> = ['proof_of_id', 'proof_of_address', 'source_of_income'];

type KycStep = {
	description: string;
	requirement: RequirementIdentifier;
	statusText: null | string;
	step: number;
	title: string;
};

type ParsedLevelStatus = 'completed' | 'notStarted' | 'retry' | 'underReview';

export const useRequirementStatuses = () => {
	const { data, isFetching: loadingStatuses, refetch: refetchRequirementStatuses } = useGetRequirementStatusesQuery();
	const { requirementIndex, requirements } = useAtomValue(customRequirementsAtom);
	const { $t } = useIntl();

	const currentRequirement = useMemo(() => requirements?.[requirementIndex], [requirements, requirementIndex]);

	const requirementStatuses = useMemo(
		() => data?.filter(requirement => KYC_REQUIREMENTS.includes(requirement.requirementIdentifier)) || [],
		[data]
	);

	const shouldShowSOI = useMemo(
		() =>
			requirements?.includes('source_of_income') ||
			requirementStatuses.find(requirement => requirement.requirementIdentifier === 'source_of_income')
				?.status === 'completed_allow',
		[requirements, requirementStatuses]
	);

	const getStatus = useCallback(
		(requirement?: RequirementIdentifier): ParsedLevelStatus => {
			if (!requirement) return 'notStarted';

			const status = requirementStatuses.find(
				({ requirementIdentifier }) => requirementIdentifier === requirement
			)?.status;

			if (!status) return 'notStarted';
			switch (status) {
				case 'completed_allow':
					return 'completed';
				case 'under_review':
				case 'waiting':
					return 'underReview';
				case 'retry':
					return 'retry';
				default:
					return 'notStarted';
			}
		},
		[requirements, requirementStatuses]
	);

	const currentRequirementStatus = useMemo(() => getStatus(currentRequirement), [getStatus, currentRequirement]);

	const getStatusText = useCallback(
		(requirement: RequirementIdentifier) => {
			const status = getStatus(requirement);
			const isCurrentRequirement = requirement === currentRequirement;

			switch (status) {
				case 'underReview':
					return $t('requirements.kyc.summary.status.underReview');
				case 'completed':
					return $t('requirements.kyc.summary.status.completed');
				case 'retry':
					return $t(
						`requirements.kyc.summary.status.${isCurrentRequirement ? 'rejectedTryAgain' : 'rejected'}`
					);
				default:
					return null;
			}
		},
		[getStatus, currentRequirement]
	);

	const kycSteps = useMemo(() => {
		const steps: Array<KycStep> = [
			{
				description: $t('requirements.kyc.summary.identity.description'),
				requirement: 'proof_of_id',
				statusText: getStatusText('proof_of_id'),
				step: 1,
				title: $t('requirements.kyc.summary.identity.title')
			},
			{
				description: $t('requirements.kyc.summary.address.description'),
				requirement: 'proof_of_address',
				statusText: getStatusText('proof_of_address'),
				step: 2,
				title: $t('requirements.kyc.summary.address.title')
			}
		];

		if (shouldShowSOI) {
			steps.push({
				description: $t('requirements.kyc.summary.income.description'),
				requirement: 'source_of_income',
				statusText: getStatusText('source_of_income'),
				step: 3,
				title: $t('requirements.kyc.summary.income.title')
			});
		}

		return steps;
	}, [shouldShowSOI, getStatusText]);

	const userIsBlocked = useMemo(
		() =>
			kycSteps.every(({ requirement }) => {
				const status = getStatus(requirement);

				return status === 'completed' || status === 'underReview';
			}),
		[kycSteps, getStatus]
	);

	const allRequirementsCompleted = useMemo(
		() => kycSteps.every(({ requirement }) => getStatus(requirement) === 'completed'),
		[kycSteps, getStatus]
	);

	const firstRetryRequirementIndex = useMemo(() => {
		if (!requirements) return -1;

		return requirements.findIndex(requirement => {
			const status = getStatus(requirement);

			return status === 'retry' || status === 'notStarted';
		});
	}, [requirements, getStatus]);

	return {
		allRequirementsCompleted,
		currentRequirement,
		currentRequirementStatus,
		firstRetryRequirementIndex,
		getStatus,
		kycSteps,
		loadingStatuses,
		refetchRequirementStatuses,
		requirementStatuses,
		userIsBlocked
	};
};
